import type { CurrentUser } from '@/types/api';
import { hasMenuPermission } from '@/utils/menuPermission';

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState:
    | { currentUser?: CurrentUser; userMenuCodes?: string[] }
    | undefined,
) {
  const { currentUser, userMenuCodes } = initialState ?? {};
  const userId = currentUser?.userid;
  const userRoles = (
    currentUser as CurrentUser & { roles?: Array<{ code?: string }> }
  )?.roles;
  const hasLedgerRole = (code: string) =>
    userRoles?.some((role) => role.code === code) ?? false;
  const canEditLedger = hasLedgerRole('LEDGER_ADMIN');
  /** 子机构台账管理员：仅可导出台账；台账管理专员同样可导出 */
  const canExportLedger =
    hasLedgerRole('LEDGER_ADMIN') || hasLedgerRole('SUB_TZGLZY');

  return {
    canAdmin: currentUser && currentUser.access === 'admin',

    // 菜单权限检查函数
    canAccessMenu: (menuName: string) => {
      if (!userMenuCodes || userMenuCodes.length === 0) {
        return false;
      }
      return hasMenuPermission(menuName, userMenuCodes, userId);
    },

    // 具体的菜单权限
    canAccessCustomer:
      hasMenuPermission('customer', userMenuCodes || [], userId) ||
      hasMenuPermission('customerAdd', userMenuCodes || [], userId) ||
      hasMenuPermission('customerEdit', userMenuCodes || [], userId) ||
      hasMenuPermission('customerView', userMenuCodes || [], userId),
    // 如果有主菜单权限，自动拥有子菜单权限
    canAccessCustomerAdd:
      hasMenuPermission('customerAdd', userMenuCodes || [], userId) ||
      hasMenuPermission('customer', userMenuCodes || [], userId),
    canAccessCustomerEdit:
      hasMenuPermission('customerEdit', userMenuCodes || [], userId) ||
      hasMenuPermission('customer', userMenuCodes || [], userId),
    canAccessCustomerView:
      hasMenuPermission('customerView', userMenuCodes || [], userId) ||
      hasMenuPermission('customer', userMenuCodes || [], userId),
    // 报告管理：如果有主菜单或任一子菜单权限，就显示主菜单
    canAccessReport:
      hasMenuPermission('report', userMenuCodes || [], userId) ||
      hasMenuPermission('reportStatistics', userMenuCodes || [], userId),
    canAccessOperations: hasMenuPermission(
      'operations',
      userMenuCodes || [],
      userId,
    ),
    canAccessFinance: hasMenuPermission('finance', userMenuCodes || [], userId),
    // 系统管理：如果有任何子菜单权限，就显示主菜单
    canAccessSystem:
      hasMenuPermission('system', userMenuCodes || [], userId) ||
      hasMenuPermission('systemOrganization', userMenuCodes || [], userId) ||
      hasMenuPermission('settings', userMenuCodes || [], userId) ||
      hasMenuPermission('systemUser', userMenuCodes || [], userId) ||
      hasMenuPermission('systemRole', userMenuCodes || [], userId) ||
      hasMenuPermission('systemPermission', userMenuCodes || [], userId) ||
      hasMenuPermission('systemMenu', userMenuCodes || [], userId) ||
      hasMenuPermission('systemLogs', userMenuCodes || [], userId) ||
      hasMenuPermission('systemDept', userMenuCodes || [], userId) ||
      hasMenuPermission('systemDistrict', userMenuCodes || [], userId) ||
      hasMenuPermission('systemBusinessSubType', userMenuCodes || [], userId) ||
      hasMenuPermission('systemFormProfile', userMenuCodes || [], userId) ||
      hasMenuPermission('systemCertRecognizeEval', userMenuCodes || [], userId),
    canAccessActivity: hasMenuPermission(
      'activity',
      userMenuCodes || [],
      userId,
    ),
    canAccessPlatform: hasMenuPermission(
      'platform',
      userMenuCodes || [],
      userId,
    ),
    // 任务管理：如果有主菜单或任一子菜单权限，就显示主菜单
    canAccessTask:
      hasMenuPermission('task', userMenuCodes || [], userId) ||
      hasMenuPermission('taskMyTask', userMenuCodes || [], userId) ||
      hasMenuPermission('taskStatistics', userMenuCodes || [], userId) ||
      hasMenuPermission('taskSupplement', userMenuCodes || [], userId) ||
      hasMenuPermission('reportReview', userMenuCodes || [], userId),

    // 任务管理子菜单权限（只根据子菜单本身的权限控制）
    canAccessTaskMyTask: hasMenuPermission(
      'taskMyTask',
      userMenuCodes || [],
      userId,
    ),
    canAccessTaskStatistics: hasMenuPermission(
      'taskStatistics',
      userMenuCodes || [],
      userId,
    ),
    canAccessTaskSupplement: hasMenuPermission(
      'taskSupplement',
      userMenuCodes || [],
      userId,
    ),
    // 净值方案：主菜单或任一子菜单有权限即可展示主菜单
    canAccessNetValueSolution:
      hasMenuPermission('netValueSolution', userMenuCodes || [], userId) ||
      hasMenuPermission('netValueSolutionList', userMenuCodes || [], userId) ||
      hasMenuPermission(
        'netValueSolutionCommonParams',
        userMenuCodes || [],
        userId,
      ),
    canAccessNetValueSolutionList:
      hasMenuPermission('netValueSolutionList', userMenuCodes || [], userId) ||
      hasMenuPermission('netValueSolution', userMenuCodes || [], userId),
    canAccessNetValueSolutionCommonParams:
      hasMenuPermission(
        'netValueSolutionCommonParams',
        userMenuCodes || [],
        userId,
      ) || hasMenuPermission('netValueSolution', userMenuCodes || [], userId),
    // 报告模板配置：主菜单或任一子菜单有权限即可展示主菜单
    canAccessReportTemplate:
      hasMenuPermission('reportTemplate', userMenuCodes || [], userId) ||
      hasMenuPermission('reportTemplateList', userMenuCodes || [], userId) ||
      hasMenuPermission(
        'reportTemplateCommonParams',
        userMenuCodes || [],
        userId,
      ),
    canAccessReportTemplateList:
      hasMenuPermission('reportTemplateList', userMenuCodes || [], userId) ||
      hasMenuPermission('reportTemplate', userMenuCodes || [], userId),
    canAccessReportTemplateCommonParams:
      hasMenuPermission(
        'reportTemplateCommonParams',
        userMenuCodes || [],
        userId,
      ) || hasMenuPermission('reportTemplate', userMenuCodes || [], userId),
    // 台账管理：如果有主菜单或任一子菜单权限，就显示主菜单
    canAccessLedger:
      hasMenuPermission('ledger', userMenuCodes || [], userId) ||
      hasMenuPermission('ledgerList', userMenuCodes || [], userId) ||
      hasMenuPermission('ledgerInquiry', userMenuCodes || [], userId),
    // 台账管理子菜单权限
    canAccessLedgerList: hasMenuPermission(
      'ledgerList',
      userMenuCodes || [],
      userId,
    ),
    canAccessLedgerInquiry: hasMenuPermission(
      'ledgerInquiry',
      userMenuCodes || [],
      userId,
    ),
    canAccessInquiryBatch: hasMenuPermission(
      'inquiryBatch',
      userMenuCodes || [],
      userId,
    ),
    /** 台账管理专员：可编辑台账、直连 */
    canEditLedger,
    canExportLedger,

    // 报告管理子菜单权限（只根据子菜单本身的权限控制）
    canAccessReportReview: hasMenuPermission(
      'reportReview',
      userMenuCodes || [],
      userId,
    ),
    canAccessReportStatistics: hasMenuPermission(
      'reportStatistics',
      userMenuCodes || [],
      userId,
    ),
    // 报告详情页：我的审核或报告统计任一权限即可访问（详情页已迁至 /task/detail）
    canAccessReportDetail:
      hasMenuPermission('reportReview', userMenuCodes || [], userId) ||
      hasMenuPermission('reportStatistics', userMenuCodes || [], userId),

    // 系统管理子菜单权限
    canAccessOrganization: hasMenuPermission(
      'systemOrganization',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemDept: hasMenuPermission(
      'systemDept',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemDistrict: hasMenuPermission(
      'systemDistrict',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemBusinessSubType: hasMenuPermission(
      'systemBusinessSubType',
      userMenuCodes || [],
      userId,
    ),
    /** 表单配置包：菜单码 systemFormProfile（需在「菜单配置」中录入并授权给角色） */
    canAccessSystemFormProfile: hasMenuPermission(
      'systemFormProfile',
      userMenuCodes || [],
      userId,
    ),
    /** 权属识别评测：菜单码 systemCertRecognizeEval */
    canAccessSystemCertRecognizeEval: hasMenuPermission(
      'systemCertRecognizeEval',
      userMenuCodes || [],
      userId,
    ),
    // 个人设置菜单对所有用户开放，包括没有角色的用户
    canAccessPersonal: true, // 个人设置菜单始终可访问
    canAccessSettings: hasMenuPermission(
      'settings',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemUser: hasMenuPermission(
      'systemUser',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemRole: hasMenuPermission(
      'systemRole',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemPermission: hasMenuPermission(
      'systemPermission',
      userMenuCodes || [],
      userId,
    ),
    canAccessSystemMenu: hasMenuPermission(
      'systemMenu',
      userMenuCodes || [],
      userId,
    ),
    canAccessLogs: hasMenuPermission('systemLogs', userMenuCodes || [], userId),
  };
}
